import { TTreeDevice } from '@/features/devices/schemas/devices.schemas.ts';

export type FlatDeviceEntry = {
  device: TTreeDevice;
  path: number[];
  depth: number;
};

export function parseRowPath(rowId: string): number[] {
  return rowId.split('.').map(Number);
}

export function getParentPath(rowId: string): string | null {
  const parts = rowId.split('.');
  return parts.length <= 1 ? null : parts.slice(0, -1).join('.');
}

export function getDeviceAtPath(devices: TTreeDevice[], path: number[]): TTreeDevice | null {
  if (path.length === 0) return null;
  const device = devices[path[0]];
  if (!device) return null;
  if (path.length === 1) return device;
  return device.subDevices ? getDeviceAtPath(device.subDevices, path.slice(1)) : null;
}

export function updateDeviceAtPath(
  devices: TTreeDevice[],
  path: number[],
  updater: (device: TTreeDevice) => TTreeDevice
): TTreeDevice[] {
  if (path.length === 0) return devices;

  return devices.map((device, index) => {
    if (index !== path[0]) return device;
    if (path.length === 1) return updater(device);
    return {
      ...device,
      subDevices: updateDeviceAtPath(device.subDevices ?? [], path.slice(1), updater),
    };
  });
}

export function removeDeviceAtPath(devices: TTreeDevice[], path: number[]): TTreeDevice[] {
  if (path.length === 0) return devices;
  if (path.length === 1) return devices.filter((_, i) => i !== path[0]);

  return devices.map((device, index) => {
    if (index !== path[0]) return device;
    return {
      ...device,
      subDevices: removeDeviceAtPath(device.subDevices ?? [], path.slice(1)),
    };
  });
}

export function appendChildAtPath(
  devices: TTreeDevice[],
  parentPath: number[],
  child: TTreeDevice
): TTreeDevice[] {
  if (parentPath.length === 0) return [...devices, child];

  return devices.map((device, index) => {
    if (index !== parentPath[0]) return device;
    if (parentPath.length === 1) {
      return {
        ...device,
        subDevices: device.subDevices ? [...device.subDevices, child] : [child],
      };
    }
    return {
      ...device,
      subDevices: appendChildAtPath(device.subDevices ?? [], parentPath.slice(1), child),
    };
  });
}

export function insertSiblingAfterPath(
  devices: TTreeDevice[],
  path: number[],
  sibling: TTreeDevice
): TTreeDevice[] {
  if (path.length === 0) return devices;

  if (path.length === 1) {
    const idx = path[0];
    return [...devices.slice(0, idx + 1), sibling, ...devices.slice(idx + 1)];
  }

  return devices.map((device, index) => {
    if (index !== path[0]) return device;
    return {
      ...device,
      subDevices: insertSiblingAfterPath(device.subDevices ?? [], path.slice(1), sibling),
    };
  });
}

export function flattenDevicesWithPath(
  devices: TTreeDevice[],
  excludePath?: number[]
): FlatDeviceEntry[] {
  const result: FlatDeviceEntry[] = [];
  const excludeKey = excludePath?.join('.');

  const walk = (list: TTreeDevice[], parentPath: number[], depth: number) => {
    for (let i = 0; i < list.length; i++) {
      const currentPath = [...parentPath, i];
      const currentKey = currentPath.join('.');

      if (
        excludeKey &&
        (currentKey === excludeKey || currentKey.startsWith(excludeKey + '.'))
      ) {
        continue;
      }

      result.push({ device: list[i], path: currentPath, depth });

      if (list[i].subDevices?.length) {
        walk(list[i].subDevices!, currentPath, depth + 1);
      }
    }
  };

  walk(devices, [], 0);
  return result;
}

export function recalculatePathAfterDelete(
  deletedPath: number[],
  targetPath: number[]
): number[] | null {
  if (
    targetPath.length >= deletedPath.length &&
    deletedPath.every((val, i) => val === targetPath[i])
  ) {
    return null;
  }

  const result = [...targetPath];
  const minLen = Math.min(deletedPath.length, targetPath.length);

  for (let i = 0; i < minLen; i++) {
    if (i < deletedPath.length - 1) {
      if (deletedPath[i] !== targetPath[i]) return result;
    } else {
      if (targetPath[i] > deletedPath[i]) {
        result[i] = targetPath[i] - 1;
      }
      break;
    }
  }

  return result;
}

export function moveDevice(
  devices: TTreeDevice[],
  sourcePath: number[],
  targetParentPath: number[] | null,
  device: TTreeDevice
): { devices: TTreeDevice[]; newPath: number[]; adjustedTargetParent: number[] | null } {
  const afterRemove = removeDeviceAtPath(devices, sourcePath);

  if (!targetParentPath) {
    return {
      devices: [...afterRemove, device],
      newPath: [afterRemove.length],
      adjustedTargetParent: null,
    };
  }

  const adjustedTarget = recalculatePathAfterDelete(sourcePath, targetParentPath);
  if (!adjustedTarget) {
    return { devices, newPath: sourcePath, adjustedTargetParent: null };
  }

  const targetDevice = getDeviceAtPath(afterRemove, adjustedTarget);
  const newPath = [...adjustedTarget, targetDevice?.subDevices?.length ?? 0];

  return {
    devices: appendChildAtPath(afterRemove, adjustedTarget, device),
    newPath,
    adjustedTargetParent: adjustedTarget,
  };
}

export function computeExpandedStateAfterMove(
  expanded: Record<string, boolean>,
  sourcePath: number[],
  newPath: number[],
  expandTargetParent?: number[]
): Record<string, boolean> {
  const oldPathStr = sourcePath.join('.');
  const newPathStr = newPath.join('.');

  if (oldPathStr === newPathStr && !expandTargetParent) {
    return expanded;
  }

  const subtreeRemap: Record<string, boolean> = {};
  for (const [key, value] of Object.entries(expanded)) {
    if (key === oldPathStr) {
      subtreeRemap[newPathStr] = value;
    } else if (key.startsWith(oldPathStr + '.')) {
      subtreeRemap[newPathStr + key.slice(oldPathStr.length)] = value;
    }
  }

  const deletedIndex = sourcePath[sourcePath.length - 1];
  const deleteParent = sourcePath.slice(0, -1).join('.');
  const shifted = shiftExpandedKeys(expanded, deleteParent, deletedIndex, -1, deletedIndex);

  if (expandTargetParent) {
    shifted[expandTargetParent.join('.')] = true;
  }

  return { ...shifted, ...subtreeRemap };
}

// eslint-disable-next-line no-secrets/no-secrets
const TOKEN_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

export type Rng = () => number;

export function buildToken(length = 4, rng: Rng = Math.random): string {
  let token = '';
  for (let i = 0; i < length; i++) {
    token += TOKEN_ALPHABET[Math.floor(rng() * TOKEN_ALPHABET.length)];
  }
  return token;
}

export function buildSuffixes(type: 'new' | 'clone', rng: Rng = Math.random) {
  const token = buildToken(4, rng);
  if (type === 'new') {
    return { name: `New | ${token}`, sn: `NEW-${token}` };
  }
  return { name: ` | Clone | ${token}`, sn: `-CLONE-${token}` };
}

export function cloneSubDevicesWithNewIdentifiers(
  subDevices: TTreeDevice[] | undefined,
  existingSns: Set<string>,
  rng: Rng = Math.random
): TTreeDevice[] | undefined {
  if (!subDevices?.length) return subDevices;
  return subDevices.map((sub) => {
    let suffixes = buildSuffixes('clone', rng);
    let candidateSn = `${sub.sn}${suffixes.sn}`;
    while (existingSns.has(candidateSn)) {
      suffixes = buildSuffixes('clone', rng);
      candidateSn = `${sub.sn}${suffixes.sn}`;
    }
    existingSns.add(candidateSn);
    return {
      ...sub,
      deviceId: '',
      connectedAt: '',
      updatedAt: '',
      createdAt: '',
      name: `${sub.name}${suffixes.name}`,
      sn: candidateSn,
      subDevices: cloneSubDevicesWithNewIdentifiers(sub.subDevices, existingSns, rng),
    };
  });
}

export function shiftExpandedKeys(
  expanded: Record<string, boolean>,
  parentPath: string,
  afterIndex: number,
  delta: number,
  removeAtIndex?: number
): Record<string, boolean> {
  const result: Record<string, boolean> = {};
  const prefix = parentPath ? parentPath + '.' : '';

  for (const [key, value] of Object.entries(expanded)) {
    if (!key.startsWith(prefix)) {
      result[key] = value;
      continue;
    }

    const rest = key.slice(prefix.length);
    const dotPos = rest.indexOf('.');
    const siblingIndexStr = dotPos === -1 ? rest : rest.slice(0, dotPos);
    const siblingIndex = Number(siblingIndexStr);

    if (isNaN(siblingIndex)) {
      result[key] = value;
      continue;
    }

    if (removeAtIndex !== undefined && siblingIndex === removeAtIndex) {
      continue;
    }

    if (siblingIndex > afterIndex) {
      const newIndex = siblingIndex + delta;
      const suffix = dotPos === -1 ? '' : rest.slice(dotPos);
      result[prefix + newIndex + suffix] = value;
    } else {
      result[key] = value;
    }
  }

  return result;
}
